import React, {ChangeEvent, forwardRef, useImperativeHandle, useState} from 'react';
import cn from "classnames";

import './EmailsList.scss';
import {Button} from 'components';

export interface IEmailsListProps {
    className?: string,
    disabled?: boolean,
    emails: string[],
    onChangeEmails: (emails: string[]) => void,
}

// These methods can be called by parent components
export interface IEmailsListAPI {
    finishEditing: () => {isValid: boolean, emails: string[]},
}

// Renders a list of emails and allows editing it as a textarea.
// We're using forwardRef to expose methods that a parent component can call.
export const EmailsList = forwardRef<IEmailsListAPI, IEmailsListProps>(function EmailsList(props, ref) {
    const { className, disabled, emails, onChangeEmails } = props;

    const [isEditing, setIsEditing] = useState(false);
    const [err, setErr] = useState('');
    const [textareaVal, setTextareaVal] = useState('');

    function onChangeTextarea(e: ChangeEvent<HTMLTextAreaElement>) {
        setTextareaVal(e.target.value);
    }

    function cancel() {
        setIsEditing(false);
        setErr('');
        setTextareaVal('');
    }

    function startEditing() {
        if (isEditing) return;

        // Convert the emails list into a string
        const emailsString = emails.join(',\n');

        setTextareaVal(emailsString);
        setIsEditing(true);
    }

    // Parses the emails string and validates each email.
    function parseEmails(emailsInput: string): {isValid: boolean, emails?: string[], error?: string} {
        // First, split by commas or whitespace.
        // Regex:
        //     \s*     optional whitespace
        //     [,\s]   comma or whitespace
        //     \s*     optional whitespace
        let lines = emailsInput.split(/\s*[,\s]\s*/)

        // Trim each line, then remove empty lines
        lines = lines.map(line => line.trim())
        lines = lines.filter(line => line.length > 0)

        // Matches "something@something.something"
        // Now make sure each line looks like an email. We're not going to be strict
        // about this because the regex for true email validation is really complicated.
        for (const line of lines) {
            // Regex:
            //     ^          beginning
            //     [^\s@]+    1+ characters that aren't whitespace or "@"
            //     @          "@" character
            //     [^\s@]+    1+ characters that aren't whitespace or "@"
            //     \.         "." character
            //     [^\s@]+    1+ characters that aren't whitespace or "@"
            //     $          end
            if (!line.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
                // Quit and show an error if it's invalid
                return {
                    isValid: false,
                    error: `"${line}" isn't a valid email.`,
                };
            }
        }

        // Deduplicate list, preserving original order
        const alreadyAdded: {[email: string]: boolean} = {};
        const emails: string[] = [];
        for (const line of lines) {
            if (!alreadyAdded[line]) {
                emails.push(line);
                alreadyAdded[line] = true;
            }
        }

        // If we get to here, all emails are valid.
        return {
            isValid: true,
            emails: emails,
        };
    }

    // If we're valid, stop editing and call our onChange callback. If not valid, show an error.
    function finishEditing(): {isValid: boolean, emails: string[]} {
        // If we aren't currently editing, we'll assume we were passed valid emails
        if (!isEditing) {
            return {
                isValid: true, 
                emails: emails,
            };
        }

        // Do validation before allowing to save
        const parseResult = parseEmails(textareaVal);
        
        if (parseResult.isValid) {
            setIsEditing(false);
            setErr('');
            onChangeEmails(parseResult.emails);
        } else {
            setErr(parseResult.error)
        }

        return {
            isValid: parseResult.isValid,
            emails: parseResult.emails,
        }
    }

    // Expose an API for parent components to call. This allows us to do validation when 
    // the user clicks the "Save Update" button, which is outside this component.
    useImperativeHandle(ref, () => ({
        finishEditing: finishEditing,
    }));

    return (
        <div className={cn('emailsList', className)}>
            {!isEditing && (
                <ul className="emailsList_list">
                    {emails.map((email, i) => (
                        <li key={i}>{email}</li>
                    ))}
                </ul>
            )}
            {isEditing && (<>
                <textarea
                    className={cn('emailsList_textarea', {
                        'error': !!err,
                    })}
                    value={textareaVal}
                    onChange={onChangeTextarea}
                    rows={5}
                ></textarea>
                {err && (
                    <div className="emailsList_error">
                        {err}
                    </div>
                )}
                <Button theme="text" size="small" onClick={cancel} className="emailsList_cancelButton">
                    Cancel
                </Button>
            </>)}

            {!isEditing && (
                <Button
                    theme="blueOutline"
                    size="small"
                    className="emailsList_editButton"
                    onClick={startEditing}
                    disabled={disabled}
                >
                    Edit Email List
                </Button>
            )}
            {isEditing && (
                <Button
                    theme="blueOutline"
                    size="small"
                    className="emailsList_confirmButton"
                    onClick={finishEditing}
                >
                    Confirm Changes
                </Button>
            )}
        </div>
    )
});